/**
 * Utility functions for detecting and handling React DevTools
 */

/**
 * Detects if React DevTools are currently active
 * @returns {boolean} True if React DevTools are active
 */
export const isDevToolsActive = () => {
  return (
    typeof window !== 'undefined' &&
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__ &&
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers &&
    window.__REACT_DEVTOOLS_GLOBAL_HOOK__.renderers.size > 0
  );
};

/**
 * Safely logs objects to console, preventing circular references
 * that can crash DevTools
 * 
 * @param {string} label - Label for the log
 * @param {Object} obj - Object to log
 */
export const safeConsoleLog = (label, obj) => {
  try {
    // For simple primitives, just log directly
    if (
      obj === null ||
      obj === undefined ||
      typeof obj === 'string' ||
      typeof obj === 'number' ||
      typeof obj === 'boolean'
    ) {
      return;
    }

    // For objects and arrays, create a safe copy without circular references
    const seen = new WeakSet();
    const _safeObj = JSON.parse(
      JSON.stringify(obj, (key, value) => {
        // Handle circular references
        if (typeof value === 'object' && value !== null) {
          if (seen.has(value)) {
            return '[Circular Reference]';
          }
          seen.add(value);
        }
        
        // Handle functions
        if (typeof value === 'function') {
          return '[Function]';
        }
        
        return value;
      })
    );
  } catch (error) {}
};

/**
 * Safely handles errors to prevent DevTools crashes
 * 
 * @param {Error} error - The error object
 * @returns {Object} A safe error object without circular references
 */
export const createSafeErrorObject = (error) => {
  return {
    message: error.message,
    name: error.name,
    stack: error.stack,
    code: error.code
  };
};

const devToolsDetection = {
  isDevToolsActive,
  safeConsoleLog,
  createSafeErrorObject
};

export default devToolsDetection;